import React, { useState, useEffect, useRef } from 'react'
import { useMediaQuery } from 'react-responsive'
import type { GraphicOverlayProps, Item } from './types'
import { withLayout } from '../../shared/Styles.js'
import Picture from '../../components/picture.js'
import Button from '../../components/button.js'
import { Heading } from '../../components/heading.js'
import { VideoModal } from '../VideoModal/index.js'
import { cn, getLocalizedPath } from '../../helpers/utils.js'
import SwiperBox from '../SwiperBox/index.js'
import { isVideo } from '../../shared/mimeType.js'
import { useExposure } from '../../hooks/useExposure.js'
import { trackUrlRef } from '../../shared/trackUrlRef.js'
import { useAiuiContext } from '../AiuiProvider/index.js'

const componentType = 'image'
const componentName = 'p1_banner'

const ItemBlock = ({
  data: item,
  configuration,
  jIndex,
}: {
  data: Item
  configuration?: any
  jIndex?: number
  spanType?: string
}) => {
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const mediaQuery = useMediaQuery({ query: '(max-width: 768px)' })

  const ref = useRef<HTMLDivElement>(null)

  const handleAspect = () => {
    switch (configuration?.num) {
      case 1:
        return 'tablet:aspect-w-[704] tablet:aspect-h-[400] laptop:aspect-w-[896] laptop:aspect-h-[384] desktop:aspect-w-[1312] desktop:aspect-h-[512] lg-desktop:aspect-w-[1664] lg-desktop:aspect-h-[640]'
      case 2:
        return 'tablet:aspect-w-[346] tablet:aspect-h-[400] laptop:aspect-w-[440] laptop:aspect-h-[384] desktop:aspect-w-[648] desktop:aspect-h-[512] lg-desktop:aspect-w-[824] lg-desktop:aspect-h-[640]'
      case 3:
        return 'tablet:aspect-w-[296] tablet:aspect-h-[400] laptop:aspect-w-[288] laptop:aspect-h-[384] desktop:aspect-w-[426] desktop:aspect-h-[512] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[640]'
      default:
        return 'tablet:aspect-w-[296] tablet:aspect-h-[400] laptop:aspect-w-[288] laptop:aspect-h-[384] desktop:aspect-w-[426] desktop:aspect-h-[512] lg-desktop:aspect-w-[544] lg-desktop:aspect-h-[640]'
    }
  }

  useExposure(ref, {
    componentType: componentType,
    componentName: componentName,
    position: jIndex,
    componentTitle: item.title,
    componentDescription: item.description,
    navigation: configuration?.activeTab,
  })

  useEffect(() => {
    setIsMobile(mediaQuery)
  }, [mediaQuery])

  const {
    theme = 'light',
    title,
    description,
    imageUrl,
    primaryButton,
    secondaryButton,
    imageMobileUrl,
    blockLink,
    video,
    youtubeId,
    isYouTube,
  } = item
  const lgButtonSize = 'lg-desktop:px-7 lg-desktop:pb-[14px] lg-desktop:pt-[15px] lg-desktop:text-[16px]'

  return (
    <div
      className={cn(
        'item-wrapper text-info-primary group relative box-border w-full overflow-hidden',
        handleAspect(),
        {
          'rounded-2xl': configuration?.shape === 'rounded',
          'aiui-dark': theme === 'dark',
          'h-[400px]': isMobile,
        },
        `text-info-primary`
      )}
      ref={ref}
    >
      {blockLink && (
        <a
          className="absolute inset-0 z-10"
          href={trackUrlRef(
            getLocalizedPath(blockLink || '', configuration?.locale || 'us'),
            `${componentType}_${componentName}`
          )}
          data-headless-type-name={`${componentType}#${componentName}`}
          data-headless-title-desc-button={`${title}#${description}`}
          data-headless-nav-position={`${configuration?.activeTab}#${jIndex}`}
          aria-hidden="true"
          tabIndex={-1}
        ></a>
      )}
      <div className="absolute inset-0">
        {isVideo(imageUrl?.mimeType) ? (
          <video autoPlay muted playsInline loop className="tablet:block hidden h-full overflow-hidden">
            <source src={imageUrl?.url} type="video/mp4" />
          </video>
        ) : (
          <Picture
            source={imageUrl?.url}
            alt={imageUrl?.alt || ''}
            className="tablet:block hidden h-full overflow-hidden"
            imgClassName="h-full transition-all duration-300 group-hover:scale-105 object-cover"
            style={{ aspectRatio: `${imageUrl?.width}/${imageUrl?.height}` }}
          />
        )}
        {isVideo(imageMobileUrl?.mimeType) ? (
          <video autoPlay muted playsInline loop className="tablet:hidden block h-full overflow-hidden">
            <source src={imageMobileUrl?.url} type="video/mp4" />
          </video>
        ) : (
          <Picture
            source={imageMobileUrl?.url || imageUrl?.url}
            alt={imageMobileUrl?.alt || imageUrl?.alt || ''}
            className="tablet:hidden block h-full overflow-hidden"
            imgClassName="h-full transition-all duration-300 object-cover"
          />
        )}
        <div
          className={cn(
            'laptop:p-6 desktop:p-[32px] laptop:gap-4 desktop:gap-6 absolute inset-x-0 bottom-0 z-20 flex w-fit flex-col gap-6 p-4',
            {
              'laptop:top-1/2 laptop:-translate-y-1/2 laptop:bottom-auto': item?.width === 'full',
            }
          )}
        >
          <div className="flex flex-col gap-1">
            <Heading size={3} as="h3" className="item-title" html={title} />
            <Heading
              as="h4"
              className="item-description desktop:text-[16px] lg-desktop:text-[18px] line-clamp-1 text-[14px]"
              html={description}
            />
          </div>
          <div className="lg-desktop:gap-3 flex gap-2">
            {secondaryButton && secondaryButton.text && (
              <Button
                aria-label={title ?? description}
                className={cn(lgButtonSize, 'link-left')}
                variant="secondary"
                as="a"
                href={trackUrlRef(
                  getLocalizedPath(secondaryButton.link || '', configuration?.locale || 'us'),
                  `${componentType}_${componentName}`
                )}
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${description}#${secondaryButton?.text}`}
                data-headless-nav-position={`${configuration?.activeTab}#${jIndex}`}
              >
                {secondaryButton.text}
                <span className="sr-only">{title ?? description}</span>
              </Button>
            )}
            {primaryButton && primaryButton.text && (
              <Button
                aria-label={title ?? description}
                className={cn(lgButtonSize, 'link-right')}
                variant="primary"
                as="a"
                href={trackUrlRef(
                  getLocalizedPath(primaryButton.link || '', configuration?.locale || 'us'),
                  `${componentType}_${componentName}`
                )}
                data-headless-type-name={`${componentType}#${componentName}`}
                data-headless-title-desc-button={`${title}#${description}#${primaryButton.text}`}
                data-headless-nav-position={`${configuration?.activeTab}#${jIndex}`}
              >
                {primaryButton.text}
              </Button>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="laptop:bottom-[32px] laptop:right-[32px] absolute bottom-[16px] right-[16px]">
          {(video?.url || youtubeId) && (
            <button
              onClick={() => {
                configuration?.onVideoPlayBtnClick?.(video?.url || youtubeId, isYouTube)
              }}
              className="laptop:size-12 flex size-8 items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-black/75"
            >
              <svg width="12" height="14" viewBox="0 0 12 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 7L0 14L0 0L12 7Z" fill="white" />
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

const GraphicOverlay = React.forwardRef<HTMLDivElement, GraphicOverlayProps>((props, ref) => {
  const { data, className, ...rest } = props
  const { shape, items = [] } = data
  const [visible, setVisible] = useState<boolean>(false)
  const [videoUrl, setVideoUrl] = useState<string>('')
  const [youTubeId, setYouTubeId] = useState<string>('')
  const { locale = 'us' } = useAiuiContext()

  const handleVideoPlayBtnClick = (url: string, isYouTube: boolean) => {
    setVisible(true)
    if (isYouTube) {
      setYouTubeId?.(url || '')
    } else {
      setVideoUrl?.(url || '')
    }
  }

  const handleSwiperShow = (width: number) => {
    const len = items?.length
    const isShow = len > 3
    const isMiddle = len > 2
    const isMobile = len > 1

    switch (true) {
      case width >= 1440:
      case width >= 1024:
        return isShow ? 3 : len
      case width >= 768:
        if (isShow || isMiddle) {
          return 2.3
        }
        return isMobile ? 2 : 1
      default:
        return isMobile ? 1.2 : 1
    }
  }

  return (
    <section
      data-ui-component-id="GraphicOverlay"
      ref={ref}
      {...rest}
      className={cn('graphicOverlayBlock text-info-primary ', className)}
    >
      {items && items.length > 0 ? (
        <SwiperBox
          className="!overflow-visible"
          id={'MultiLayoutGraphicBlock3'}
          data={{
            list: items || [],
            configuration: {
              shape: shape,
              num: items.length,
              locale,
              onVideoPlayBtnClick: handleVideoPlayBtnClick,
            },
          }}
          Slide={ItemBlock}
          breakpoints={{
            0: {
              spaceBetween: 12,
              freeMode: false,
              slidesPerView: 1,
            },
            374: {
              spaceBetween: 12,
              freeMode: false,
              slidesPerView: handleSwiperShow(374),
            },
            768: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: handleSwiperShow(768),
            },
            1024: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: handleSwiperShow(1024),
            },
            1440: {
              spaceBetween: 16,
              freeMode: false,
              slidesPerView: handleSwiperShow(1440),
            },
          }}
        />
      ) : null}
      {visible && (
        <VideoModal
          visible={visible}
          youTubeId={youTubeId}
          videoUrl={videoUrl}
          onCloseModal={() => setVisible(false)}
        />
      )}
    </section>
  )
})

GraphicOverlay.displayName = 'GraphicOverlayBlock'

export default withLayout(GraphicOverlay)
export type { GraphicOverlayProps }
